// Example: Load Rails libraries in Vite.
//
// import * as Turbo from '@hotwired/turbo'
// Turbo.start()
//
// import ActiveStorage from '@rails/activestorage'
// ActiveStorage.start()
//
// // Import all channels.
// const channels = import.meta.glob('./**/*_channel.js', { eager: true })

// Example: Import a stylesheet in app/frontend/index.css
// import '~/index.css'

function loadContentScreenEditor() {
  if (!document.querySelector("[data-content-screen-editor]")) return;
  import("./content_screen_editor");
}

function loadContentScreenPreview() {
  if (!document.querySelector("[data-content-screen-preview]")) return;
  import("./content_screen_preview");
}

function initContentScreenPreviewModal(root = document) {
  const modal = root.querySelector("[data-content-screen-preview-modal]");
  if (!modal || modal.dataset.contentScreenPreviewModalBound === "true") return;

  modal.dataset.contentScreenPreviewModalBound = "true";
  const openButtons = Array.from(document.querySelectorAll("[data-content-screen-preview-modal-open]"));
  const closeButtons = Array.from(modal.querySelectorAll("[data-content-screen-preview-modal-close]"));
  let lastTrigger = null;

  function closeModal() {
    modal.hidden = true;
    document.body.classList.remove("content-screen-preview-modal-open");
    document.removeEventListener("keydown", handleKeydown);
    if (lastTrigger) lastTrigger.focus({ preventScroll: true });
  }

  function handleKeydown(event) {
    if (event.key === "Escape") closeModal();
  }

  function openModal(event) {
    event.preventDefault();
    lastTrigger = event.currentTarget;
    modal.hidden = false;
    document.body.classList.add("content-screen-preview-modal-open");
    document.addEventListener("keydown", handleKeydown);
    const closeButton = modal.querySelector("[data-content-screen-preview-modal-close]:not(.content-screen-preview-modal-backdrop)");
    if (closeButton) closeButton.focus({ preventScroll: true });
  }

  openButtons.forEach((button) => button.addEventListener("click", openModal));
  closeButtons.forEach((button) => button.addEventListener("click", closeModal));
}

function loadArticleEditor(root = document) {
  if (!root.querySelector("[data-article-editor]")) return;
  import("./article_editor").then((module) => {
    if (module && module.initArticleEditors) module.initArticleEditors(root);
  });
}

function loadMailoutEditor(root = document) {
  if (!root.querySelector("[data-mailout-editor]")) return;
  import("./mailout_editor").then((module) => {
    if (module && module.initMailoutEditors) module.initMailoutEditors(root);
  });
}

function loadNotificationTemplateEditor() {
  if (!document.querySelector("[data-template-editor]")) return;
  import("../components/templates/TemplateEditorApp");
}

function loadTicketComposerEditor() {
  if (!document.querySelector("[data-ticket-rich-editor]")) return;
  import("./ticket_composer_editor");
}

function loadTicketSubjectEditor() {
  if (!document.querySelector("[data-ticket-subject-editor]")) return;
  import("./ticket_subject_editor");
}

function loadSimpleWysiwyg() {
  if (!document.querySelector("[data-simple-wysiwyg]")) return;
  import("./simple_wysiwyg").then((module) => {
    if (module && module.initSimpleWysiwygEditors) module.initSimpleWysiwygEditors(document);
  });
}

function loadTicketWhiteboard() {
  if (!document.querySelector("[data-ticket-whiteboard]")) return;
  import("./ticket_whiteboard").then((module) => {
    if (module && module.initTicketWhiteboards) module.initTicketWhiteboards(document);
  });
}

function loadTicketAiTools() {
  if (!document.querySelector("[data-ticket-ai-tools]")) return;
  import("./ticket_ai_tools").then((module) => {
    if (module && module.initTicketAiTools) module.initTicketAiTools(document);
  });
}

function loadTicketThreads() {
  if (!document.querySelector("[data-ticket-new-thread]")) return;
  import("./ticket_threads").then((module) => {
    if (module && module.initTicketThreadMenus) module.initTicketThreadMenus(document);
  });
}

function scrollTicketShowToBottom() {
  if (!document.querySelector("[data-ticket-show-scroll-bottom]")) return;
  if (window.location.hash) return;

  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }

  const scrollBottom = () => {
    const bottom = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight
    );
    window.scrollTo({ top: bottom, behavior: "auto" });
  };

  window.requestAnimationFrame(() => {
    scrollBottom();
    [50, 150, 350, 700].forEach((delay) => window.setTimeout(scrollBottom, delay));
  });
}

function loadBookingScheduler() {
  if (!document.querySelector("[data-booking-scheduler]")) return;
  import("./booking_scheduler");
}

function loadAttendanceTimeline() {
  if (!document.querySelector("[data-attendance-timeline]")) return;
  import("./attendance_timeline");
}

function loadAgreementOverview() {
  if (!document.querySelector("[data-agreement-overview]")) return;
  import("./agreement_overview");
}

function loadLocalOfferEditor(root = document) {
  if (!root.querySelector("[data-local-offer-editor]")) return;
  import("./local_offer_editor").then((module) => {
    if (module && module.initLocalOfferEditors) module.initLocalOfferEditors(root);
  });
}

function initLocalOfferTermsModal(root = document) {
  const modal = root.querySelector("[data-local-offer-terms-modal]");
  if (!modal || modal.dataset.localOfferTermsModalBound === "true") return;

  modal.dataset.localOfferTermsModalBound = "true";
  const openButtons = Array.from(document.querySelectorAll("[data-local-offer-terms-open]"));
  const closeButtons = Array.from(modal.querySelectorAll("[data-local-offer-terms-close]"));
  let lastTrigger = null;

  function closeModal() {
    modal.hidden = true;
    document.removeEventListener("keydown", handleKeydown);
    if (lastTrigger) lastTrigger.focus();
  }

  function handleKeydown(event) {
    if (event.key === "Escape") closeModal();
  }

  function openModal(event) {
    event.preventDefault();
    event.stopPropagation();
    lastTrigger = event.currentTarget;
    modal.hidden = false;
    document.addEventListener("keydown", handleKeydown);
    const closeButton = modal.querySelector("[data-local-offer-terms-close]:not(.local-offer-terms-modal-backdrop)");
    if (closeButton) closeButton.focus({ preventScroll: true });
  }

  openButtons.forEach((button) => button.addEventListener("click", openModal));
  closeButtons.forEach((button) => button.addEventListener("click", closeModal));
}

function initClipboardButtons(root = document) {
  root.querySelectorAll("[data-copy-text]").forEach((button) => {
    if (button.dataset.copyButtonBound === "true") return;
    button.dataset.copyButtonBound = "true";
    const originalText = button.textContent;

    button.addEventListener("click", async () => {
      const value = button.dataset.copyText || "";
      if (!value) return;

      try {
        await navigator.clipboard.writeText(value);
        button.classList.add("is-copied");
        button.querySelector("span") ? button.querySelector("span").textContent = "Copied" : button.textContent = "Copied";
        window.setTimeout(() => {
          button.classList.remove("is-copied");
          if (button.querySelector("span")) button.querySelector("span").textContent = "Copy";
          else button.textContent = originalText;
        }, 1800);
      } catch {
        window.prompt("Copy link", value);
      }
    });
  });
}

function initCopyLinkButtons(root = document) {
  root.querySelectorAll("[data-copy-link-url]").forEach((button) => {
    if (button.dataset.copyLinkBound === "true") return;
    button.dataset.copyLinkBound = "true";
    const label = button.querySelector("span");
    const originalText = label ? label.textContent : button.textContent;

    button.addEventListener("click", async () => {
      const url = button.dataset.copyLinkUrl || "";
      if (!url) return;

      button.disabled = true;
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Accept": "application/json",
            "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
          },
          credentials: "same-origin"
        });
        if (!response.ok) throw new Error("Unable to create link");

        const payload = await response.json();
        const value = payload.access_url || "";
        if (!value) throw new Error("Missing link");

        try {
          await navigator.clipboard.writeText(value);
          button.classList.add("is-copied");
          if (label) label.textContent = "Copied";
          else button.textContent = "Copied";
          button.closest("details")?.removeAttribute("open");
          window.setTimeout(() => {
            button.classList.remove("is-copied");
            if (label) label.textContent = originalText;
            else button.textContent = originalText;
          }, 1800);
        } catch {
          window.prompt("Copy contributor link", value);
        }
      } catch {
        window.alert("Could not create the contributor link. Please try again.");
      } finally {
        button.disabled = false;
      }
    });
  });
}

function initArticleUnsavedWarnings(root = document) {
  root.querySelectorAll("[data-article-unsaved-warning]").forEach((form) => {
    if (form.dataset.articleUnsavedWarningBound === "true") return;
    form.dataset.articleUnsavedWarningBound = "true";

    const markDirty = () => {
      form.dataset.articleDirty = "true";
    };

    form.addEventListener("input", markDirty);
    form.addEventListener("change", markDirty);
    form.addEventListener("submit", () => {
      form.dataset.articleDirty = "false";
    });
  });
}

function initArticleTabs(root = document) {
  root.querySelectorAll("[data-article-tabs]").forEach((container) => {
    if (container.dataset.articleTabsBound === "true") return;
    container.dataset.articleTabsBound = "true";

    const buttons = Array.from(container.querySelectorAll("[data-article-tab]"));
    const panels = Array.from(container.querySelectorAll("[data-article-tab-panel]"));
    const activate = (tab) => {
      buttons.forEach((button) => {
        const active = button.dataset.articleTab === tab;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-selected", active ? "true" : "false");
        button.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.articleTabPanel !== tab;
      });
    };

    buttons.forEach((button) => {
      button.addEventListener("click", () => activate(button.dataset.articleTab));
      button.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
        event.preventDefault();
        const currentIndex = buttons.indexOf(button);
        const offset = event.key === "ArrowRight" ? 1 : -1;
        const nextButton = buttons[(currentIndex + offset + buttons.length) % buttons.length];
        nextButton.focus();
        activate(nextButton.dataset.articleTab);
      });
    });

    activate(container.dataset.initialTab || buttons[0]?.dataset.articleTab || "details");
  });
}

function initAdminFlashLayer(root = document) {
  root.querySelectorAll(".admin-flash-layer .admin-flash").forEach((flash) => {
    if (flash.dataset.adminFlashBound === "true") return;
    flash.dataset.adminFlashBound = "true";

    const dismiss = () => {
      if (!flash.isConnected || flash.dataset.adminFlashDismissed === "true") return;
      flash.dataset.adminFlashDismissed = "true";
      flash.classList.add("is-dismissing");
      window.setTimeout(() => {
        const layer = flash.closest(".admin-flash-layer");
        flash.remove();
        if (layer && !layer.querySelector(".admin-flash")) layer.remove();
      }, 180);
    };

    flash.querySelector("[data-admin-flash-close]")?.addEventListener("click", dismiss);
    window.setTimeout(dismiss, 2000);
  });
}

function initEvolutionUnitMappingFilters(root = document) {
  root.querySelectorAll("[data-evolution-unit-mapping-filter]").forEach((container) => {
    if (container.dataset.evolutionUnitMappingFilterBound === "true") return;
    container.dataset.evolutionUnitMappingFilterBound = "true";

    const buttons = Array.from(container.querySelectorAll("[data-evolution-unit-type-filter]"));
    const rows = Array.from(container.querySelectorAll("[data-evolution-unit-type-row]"));
    const emptyRow = container.querySelector("[data-evolution-unit-type-empty]");

    const applyFilter = (unitType) => {
      let visibleCount = 0;
      rows.forEach((row) => {
        const visible = unitType === "all" || row.dataset.evolutionUnitTypeRow === unitType;
        row.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      buttons.forEach((button) => {
        const active = button.dataset.evolutionUnitTypeFilter === unitType;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", active ? "true" : "false");
      });

      if (emptyRow) emptyRow.hidden = visibleCount > 0;
    };

    buttons.forEach((button) => {
      button.setAttribute("aria-pressed", button.classList.contains("is-active") ? "true" : "false");
      button.addEventListener("click", () => applyFilter(button.dataset.evolutionUnitTypeFilter || "all"));
    });
  });
}

window.addEventListener("beforeunload", (event) => {
  const dirtyForm = document.querySelector("[data-article-unsaved-warning][data-article-dirty='true']");
  if (!dirtyForm) return;

  event.preventDefault();
  event.returnValue = "";
});

document.addEventListener("DOMContentLoaded", loadContentScreenEditor);
document.addEventListener("turbo:load", loadContentScreenEditor);
document.addEventListener("DOMContentLoaded", loadContentScreenPreview);
document.addEventListener("turbo:load", loadContentScreenPreview);
document.addEventListener("DOMContentLoaded", () => initContentScreenPreviewModal());
document.addEventListener("turbo:load", () => initContentScreenPreviewModal());
document.addEventListener("DOMContentLoaded", () => loadArticleEditor());
document.addEventListener("turbo:load", () => loadArticleEditor());
document.addEventListener("DOMContentLoaded", () => loadMailoutEditor());
document.addEventListener("turbo:load", () => loadMailoutEditor());
document.addEventListener("DOMContentLoaded", loadNotificationTemplateEditor);
document.addEventListener("turbo:load", loadNotificationTemplateEditor);
document.addEventListener("DOMContentLoaded", loadTicketComposerEditor);
document.addEventListener("turbo:load", loadTicketComposerEditor);
document.addEventListener("DOMContentLoaded", loadTicketSubjectEditor);
document.addEventListener("turbo:load", loadTicketSubjectEditor);
document.addEventListener("DOMContentLoaded", loadSimpleWysiwyg);
document.addEventListener("turbo:load", loadSimpleWysiwyg);
document.addEventListener("DOMContentLoaded", loadTicketWhiteboard);
document.addEventListener("turbo:load", loadTicketWhiteboard);
document.addEventListener("DOMContentLoaded", loadTicketAiTools);
document.addEventListener("turbo:load", loadTicketAiTools);
document.addEventListener("DOMContentLoaded", loadTicketThreads);
document.addEventListener("turbo:load", loadTicketThreads);
document.addEventListener("DOMContentLoaded", scrollTicketShowToBottom);
document.addEventListener("turbo:load", scrollTicketShowToBottom);
document.addEventListener("DOMContentLoaded", loadBookingScheduler);
document.addEventListener("turbo:load", loadBookingScheduler);
document.addEventListener("DOMContentLoaded", loadAttendanceTimeline);
document.addEventListener("turbo:load", loadAttendanceTimeline);
document.addEventListener("DOMContentLoaded", loadAgreementOverview);
document.addEventListener("turbo:load", loadAgreementOverview);
document.addEventListener("DOMContentLoaded", () => loadLocalOfferEditor());
document.addEventListener("turbo:load", () => loadLocalOfferEditor());
document.addEventListener("DOMContentLoaded", () => initLocalOfferTermsModal());
document.addEventListener("turbo:load", () => initLocalOfferTermsModal());
document.addEventListener("DOMContentLoaded", () => initClipboardButtons());
document.addEventListener("turbo:load", () => initClipboardButtons());
document.addEventListener("DOMContentLoaded", () => initCopyLinkButtons());
document.addEventListener("turbo:load", () => initCopyLinkButtons());
document.addEventListener("DOMContentLoaded", () => initArticleUnsavedWarnings());
document.addEventListener("turbo:load", () => initArticleUnsavedWarnings());
document.addEventListener("DOMContentLoaded", () => initArticleTabs());
document.addEventListener("turbo:load", () => initArticleTabs());
document.addEventListener("DOMContentLoaded", () => initAdminFlashLayer());
document.addEventListener("turbo:load", () => initAdminFlashLayer());
document.addEventListener("DOMContentLoaded", () => initEvolutionUnitMappingFilters());
document.addEventListener("turbo:load", () => initEvolutionUnitMappingFilters());
document.addEventListener("flextech:drawer-loaded", (event) => {
  loadLocalOfferEditor(event.detail && event.detail.panel ? event.detail.panel : document);
  initClipboardButtons(event.detail && event.detail.panel ? event.detail.panel : document);
  initCopyLinkButtons(event.detail && event.detail.panel ? event.detail.panel : document);
});
